import { PrimaryDataSet, EphemeralDataSet, DataValue } from '../../types';
import { createColumnId } from '../../types';
import { DataAccessor, DataState, DataAccessorOptions } from '../../types/data-accessor.types';

/**
 * PrimaryDataSet用のDataAccessor実装
 * PrimaryDataSetとEphemeralDataSetは常にデータを持っているため、同期的にアクセス可能
 */
export class PrimaryDataAccessor implements DataAccessor {
  private nameToIdMap: Map<string, string>;
  private idToNameMap: Map<string, string>;

  constructor(
    private dataset: PrimaryDataSet | EphemeralDataSet,
    private options: DataAccessorOptions = {}
  ) {
    this.nameToIdMap = new Map();
    this.idToNameMap = new Map();
    
    // スキーマから列名→列IDマッピングを作成
    this.dataset.schema?.columns?.forEach(col => {
      this.nameToIdMap.set(col.name, col.id);
      this.idToNameMap.set(col.id, col.name);
    });
  }

  isDataAvailable(): boolean {
    return true; // PrimaryDataSetは常にデータを持つ
  }

  getDataState(): DataState {
    return 'loaded'; // PrimaryDataSetは常にロード済み
  }

  async ensureDataLoaded(): Promise<PrimaryDataSet | EphemeralDataSet> {
    // PrimaryDataSetとEphemeralDataSetは常にデータを持つため、そのまま返す
    return Promise.resolve(this.dataset);
  }

  async getColumnData(columnIdOrName: string): Promise<DataValue[]> {
    return Promise.resolve(this.getColumnDataSync(columnIdOrName));
  }

  getColumnDataSync(columnIdOrName: string): DataValue[] {
    // 列名の場合は列IDに変換
    const columnId = this.nameToIdMap.has(columnIdOrName) 
      ? this.nameToIdMap.get(columnIdOrName)! 
      : columnIdOrName;

    const data = this.dataset.data[createColumnId(columnId)];
    if (!data) {
      const columnName = this.idToNameMap.get(columnId) || columnIdOrName;
      const error = new Error(`Column data for "${columnName}" (ID: ${columnId}) not found in dataset`);
      if (this.options.onError) {
        this.options.onError(error);
      }
      throw error;
    }

    return data;
  }

  async getRowData(rowIndex: number): Promise<Record<string, DataValue>> {
    const row: Record<string, DataValue> = {};
    
    this.dataset.schema?.columns?.forEach(col => {
      const columnData = this.dataset.data[col.id];
      const value = columnData?.[rowIndex];
      row[col.name] = (value !== undefined) ? value : null;
    });
    
    return Promise.resolve(row);
  }

  async getValue(rowIndex: number, columnIdOrName: string): Promise<DataValue> {
    const columnId = this.nameToIdMap.has(columnIdOrName) 
      ? this.nameToIdMap.get(columnIdOrName)! 
      : columnIdOrName;

    const columnData = this.dataset.data[createColumnId(columnId)];
    if (!columnData) {
      const columnName = this.idToNameMap.get(columnId) || columnIdOrName;
      throw new Error(`Column data for "${columnName}" (ID: ${columnId}) not found in dataset`);
    }

    const value = columnData[rowIndex];
    return Promise.resolve((value !== undefined) ? value : null);
  }

  getSchema() {
    return this.dataset.schema;
  }

  getColumnInfo(columnIdOrName: string) {
    const columnId = this.nameToIdMap.has(columnIdOrName) 
      ? this.nameToIdMap.get(columnIdOrName)! 
      : columnIdOrName;

    return this.dataset.schema?.columns?.find(col => 
      col.id === columnId || col.name === columnIdOrName
    );
  }

  getColumnNames(): string[] {
    return this.dataset.schema?.columns?.map(col => col.name) || [];
  }

  getRowCount(): number {
    return this.dataset.rowCount || 0;
  }

  hasColumn(columnName: string): boolean {
    return this.nameToIdMap.has(columnName);
  }

  getColumnId(columnName: string): string | undefined {
    return this.nameToIdMap.get(columnName);
  }

  getColumnName(columnId: string): string | undefined {
    return this.idToNameMap.get(columnId);
  }

  /**
   * データセット整合性の検証
   */
  validateConsistency(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // スキーマとデータキーの一致確認
    const schemaColumnIds = new Set(this.dataset.schema?.columns?.map(col => col.id) || []);
    const dataKeys = new Set(Object.keys(this.dataset.data));
    
    const missingInData = [...schemaColumnIds].filter(id => !dataKeys.has(id));
    const extraInData = [...dataKeys].filter(key => !schemaColumnIds.has(createColumnId(key)));
    
    if (missingInData.length > 0) {
      errors.push(`Missing data for column IDs: ${missingInData.join(', ')}`);
    }
    
    if (extraInData.length > 0) {
      errors.push(`Extra data keys not in schema: ${extraInData.join(', ')}`);
    }

    // 各列のデータ長が行数と一致するかチェック
    const expectedRowCount = this.dataset.rowCount || 0;
    
    this.dataset.schema?.columns?.forEach(col => {
      const columnData = this.dataset.data[col.id];
      if (columnData && columnData.length !== expectedRowCount) {
        errors.push(`Column "${col.name}" (ID: ${col.id}) has ${columnData.length} rows, expected ${expectedRowCount}`);
      }
    });

    // 重大な整合性エラーの場合は例外を投げる
    if (errors.length > 0) {
      const criticalErrors = errors.filter(error => 
        error.includes('Missing data for column IDs') || 
        error.includes('has 0 rows') ||
        error.includes('undefined') ||
        error.includes('null')
      );
      
      if (criticalErrors.length > 0) {
        const error = new Error(`Critical dataset corruption detected: ${criticalErrors.join('; ')}`);
        if (this.options.onError) {
          this.options.onError(error);
        }
        throw error;
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}